import * as React from 'react'
import { motion } from 'framer-motion'
import { Check } from 'lucide-react'
import { cn } from '../../lib/utils'

export interface SelectableCardProps {
  children: React.ReactNode
  selected?: boolean
  disabled?: boolean
  onClick?: () => void
  className?: string
  icon?: React.ReactNode
  title?: string
  description?: string
}

/**
 * SelectableCard - Used for selection UI like tone, hook, and style cards.
 * Features clean visual states: default, hover, selected, disabled.
 */
export function SelectableCard({
  children,
  selected = false,
  disabled = false,
  onClick,
  className,
  icon,
  title,
  description,
}: SelectableCardProps) {
  return (
    <motion.button
      type="button"
      onClick={disabled ? undefined : onClick}
      disabled={disabled}
      whileTap={disabled ? undefined : { scale: 0.99 }}
      className={cn(
        'relative w-full text-left rounded-md p-4 transition-all duration-150',
        'border bg-card',
        'focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary/40',
        {
          'border-border-subtle hover:border-border hover:bg-card-hover': !selected && !disabled,
          'border-primary/30 bg-primary/[0.04]': selected,
          'opacity-50 cursor-not-allowed': disabled,
          'cursor-pointer': !disabled,
        },
        className
      )}
    >
      {/* Selection indicator */}
      {selected && (
        <motion.div
          initial={{ scale: 0, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 0.15 }}
          className="absolute top-3 right-3 w-5 h-5 rounded-full flex items-center justify-center bg-primary"
        >
          <Check className="w-3 h-3 text-foreground" strokeWidth={3} />
        </motion.div>
      )}

      {/* Content layout */}
      <div className="flex items-start gap-3">
        {icon && (
          <div
            className={cn(
              'flex-shrink-0 w-9 h-9 rounded-lg flex items-center justify-center',
              'bg-muted text-muted-foreground',
              selected && 'text-primary bg-primary/10'
            )}
          >
            {icon}
          </div>
        )}
        <div className="flex-1 min-w-0">
          {title && (
            <div className={cn(
              'text-sm font-medium mb-0.5',
              selected ? 'text-foreground' : 'text-foreground/80'
            )}>
              {title}
            </div>
          )}
          {description && (
            <div className="text-xs font-medium text-muted-foreground line-clamp-2">
              {description}
            </div>
          )}
          {children}
        </div>
      </div>
    </motion.button>
  )
}

/**
 * SelectableCardGroup - Container for a group of selectable cards
 */
export function SelectableCardGroup({
  children,
  className,
  columns = 2,
}: {
  children: React.ReactNode
  className?: string
  columns?: 1 | 2 | 3 | 4
}) {
  const gridCols = {
    1: 'grid-cols-1',
    2: 'grid-cols-1 sm:grid-cols-2',
    3: 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3',
    4: 'grid-cols-2 sm:grid-cols-4',
  }

  return (
    <div className={cn('grid gap-2', gridCols[columns], className)}>
      {children}
    </div>
  )
}

export default SelectableCard
